import { lazy, Suspense, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { AlertTriangle, Loader2, Sparkles } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { useAuthStore, useUIStore } from '@/stores';
import { STAGE_META } from '@/constants/automation';
import { useAutomationProgress } from '@/hooks/useAutomationProgress';
import { useSetupGate } from '@/hooks/useSetupGate';
import { styles } from './BackgroundProcessIndicator.style';

// Mounted only once opened: the dialog pulls in the whole Radix dialog tree and
// its own socket subscription, neither of which a closed dialog needs.
const AutomationStatusDialog = lazy(() =>
  import('./AutomationStatusDialog').then(m => ({ default: m.AutomationStatusDialog }))
);

/**
 * Sidebar setup affordance, above the Create New button.
 *
 * While a run is going it mirrors the app-shell banner in miniature — stage,
 * overall percent, and a meter — so the progress stays anchored somewhere
 * permanent even when the user scrolls or collapses the rail. Clicking opens
 * the full status dialog.
 */
export function BackgroundProcessIndicator() {
  const collapsed = useUIStore(s => s.sidebarCollapsed);
  const { hasSkippedOnboarding, hasCompletedOnboarding } = useAuthStore();
  const progress = useAutomationProgress();
  const { reason } = useSetupGate();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Open the status dialog inline. We deliberately do NOT re-enter OnboardingFlow
  // here — that path caused the "complete steps twice / automation already in
  // progress" bug. The dialog handles running, completed, failed, and
  // not-yet-started cases without route switching.
  const handleClick = () => setDialogOpen(true);

  const isIncomplete = hasSkippedOnboarding && !hasCompletedOnboarding;
  if (!isIncomplete) return null;

  const hasFailed = Boolean(progress.error ?? progress.erroredStage);
  const isRunning = progress.isActive && !hasFailed;

  const dialog = dialogOpen && (
    <Suspense fallback={null}>
      <AutomationStatusDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </Suspense>
  );

  if (hasFailed) {
    return (
      <>
        <div className={styles.wrap}>
          <button
            onClick={handleClick}
            className={collapsed ? styles.failedCollapsed : styles.failedExpanded}
            title="Setup issue"
            aria-label="Setup progress"
          >
            {collapsed ? (
              <AlertTriangle className="size-4 text-red-500" />
            ) : (
              <div className="flex items-center gap-2.5">
                <AlertTriangle className="size-4 shrink-0 text-red-500" />
                <span className={styles.failedLabel}>Setup issue</span>
              </div>
            )}
          </button>
        </div>
        {dialog}
      </>
    );
  }

  if (isRunning) {
    const stage = progress.currentStage;
    const label = stage ? STAGE_META[stage.stage].shortLabel : 'Checking setup';
    const Icon = stage ? STAGE_META[stage.stage].icon : Loader2;

    if (collapsed) {
      return (
        <>
          <div className={styles.wrap}>
            <button
              onClick={handleClick}
              className={styles.runningCollapsed}
              title={`${label} — ${progress.overallPercent}%`}
              aria-label="Setup progress"
            >
              <Loader2 className="size-4 animate-spin text-purple-600" />
              <span className={styles.percentCollapsed}>{progress.overallPercent}%</span>
            </button>
          </div>
          {dialog}
        </>
      );
    }

    return (
      <>
        <div className={styles.wrap}>
          <button
            onClick={handleClick}
            className={styles.runningExpanded}
            aria-label="Setup progress"
          >
            <div className="flex items-center gap-2.5">
              <Loader2 className="size-4 shrink-0 animate-spin text-purple-600" />
              <AnimatePresence mode="wait">
                <motion.span
                  key={stage?.stage ?? 'pending'}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.2 }}
                  className="flex min-w-0 items-center gap-1.5"
                >
                  <Icon className="size-3.5 shrink-0 text-purple-600" />
                  <span className={styles.stageLabel}>{label}</span>
                </motion.span>
              </AnimatePresence>
              <span className={styles.percent}>{progress.overallPercent}%</span>
            </div>
            <Progress value={progress.overallPercent} className={styles.meter} />
          </button>
        </div>
        {dialog}
      </>
    );
  }

  // No run has ever started. Two very different reasons land here:
  //   • needs_plan — the wizard IS finished; automation just cannot start
  //     without an active plan. Nudge toward checkout, never back into steps.
  //   • wizard_incomplete (or still checking) — an answer is genuinely
  //     missing, so the wizard really is resumable.
  const idleLabel = reason === 'needs_plan' ? 'Subscribe to start' : 'Complete Setup';
  return (
    <>
      <div className={styles.wrap}>
        <button
          onClick={handleClick}
          className={collapsed ? styles.idleCollapsed : styles.idleExpanded}
          title={idleLabel}
        >
          {collapsed ? (
            <Sparkles className="size-4 text-amber-600" />
          ) : (
            <div className="flex items-center gap-2.5">
              <Sparkles className="size-4 shrink-0 text-amber-600" />
              <span className={styles.idleLabel}>{idleLabel}</span>
            </div>
          )}
        </button>
      </div>
      {dialog}
    </>
  );
}
